import * as React from 'react';
import ModalClose from './ModalClose';
import { Button, Modal, ModalBody } from 'reactstrap';
import Spinner from './Spinner';
import ExclamationCircleSVG from './svg/ExclamationCircleSVG';
import CheckSVG from './svg/CheckSVG';
import ExclamationCircleSVG2 from './svg/ExclamationCircleSVGAlternative';
import ExclamationTriangleSVGAlternative from './svg/ExclamationTriangleSVGAlternative';
import { AlertSize } from './Alert';
import classnames from 'classnames';

export interface RenderButtonProps {
	onConfirm: () => void;
	onCancel: () => void;
	confirmButtonText?: string;
	cancelButtonText?: string;
	color: ConfirmColor;
	confirmButtonColor: ConfirmColor;
	cancelButtonColor: ConfirmColor;
	inProgress?: boolean;
	cancelTestId: string;
	confirmTestId: string;
}

/*
 * Types of colors for confirm
 */
export type ConfirmColor = 'default' | 'primary' | 'success' | 'info' | 'warning' | 'danger';

export interface ConfirmProps {
	isOpen: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	renderTitle: () => React.ReactNode;
	renderContents: () => React.ReactNode;
	renderButtons: (props: RenderButtonProps) => React.ReactNode;
	confirmButtonText?: string;
	cancelButtonText?: string;
	color?: ConfirmColor;
	inProgress?: boolean;
	size?: AlertSize;
	cancelTestId?: string;
	confirmTestId?: string;
	overflowVisible?: boolean;
}

const fancyModalSVGClass = 'fancy-modal__footer-icon-svg';

/**
 * Reusable confirm component that replaces window confirm().
 *
 * @class Confirm
 * @extends {React.Component<ConfirmProps>}
 */
class Confirm extends React.Component<ConfirmProps> {
	public static defaultProps = {
		renderButtons: (renderButtonProps: RenderButtonProps) => (
			<>
				<div className="fancy-modal__footer-icon">
					{renderButtonProps.color === 'default' ||
					renderButtonProps.color === 'primary' ||
					renderButtonProps.color === 'success' ? (
						<CheckSVG class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--success'} />
					) : renderButtonProps.color === 'info' ? (
						<ExclamationCircleSVG2 class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--info'} />
					) : renderButtonProps.color === 'warning' ? (
						<ExclamationTriangleSVGAlternative
							class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--warning'}
						/>
					) : renderButtonProps.color === 'danger' ? (
						<ExclamationCircleSVG class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg--danger'} />
					) : (
						<ExclamationCircleSVG class={fancyModalSVGClass + ' fancy-modal__footer-icon-svg'} />
					)}
				</div>
				<div className="fancy-modal__footer-actions">
					<Button
						color={renderButtonProps.cancelButtonColor}
						className="btn-inline btn-modal-action fancy-modal__cancel-btn"
						outline
						onClick={renderButtonProps.onCancel}
						data-testid={renderButtonProps.cancelTestId}
					>
						{renderButtonProps.cancelButtonText}
					</Button>
					<Button
						color={renderButtonProps.confirmButtonColor}
						className="btn-modal-action fancy-modal__confirm-btn"
						outline={renderButtonProps.color === 'primary' ? true : false}
						onClick={renderButtonProps.onConfirm}
						disabled={renderButtonProps.inProgress}
						data-testid={renderButtonProps.confirmTestId}
					>
						{renderButtonProps.inProgress ? (
							<Spinner centered color="light" />
						) : (
							renderButtonProps.confirmButtonText
						)}
					</Button>
				</div>
			</>
		),
	};

	public render() {
		const {
			isOpen,
			onCancel,
			onConfirm,
			renderTitle,
			renderContents,
			renderButtons,
			color = 'warning',
			confirmButtonText = 'Ok',
			cancelButtonText = 'Cancel',
			inProgress,
			size = 'small',
			cancelTestId = 'confirm-alert-cancel',
			confirmTestId = 'confirm-alert-confirm',
			overflowVisible = false,
		} = this.props;

		const confirmBaseClass = 'fancy-modal ';
		const confirmColor = 'fancy-modal--' + color;
		const combinedClass = confirmBaseClass + confirmColor;

		let confirmButtonColor;
		let cancelButtonColor;

		// Set the button colors based on the confirm's color.
		switch (color) {
			case 'default':
				confirmButtonColor = 'primary';
				cancelButtonColor = 'primary-alt';
				break;

			case 'primary':
			case 'info':
			case 'success':
			case 'danger':
			case 'warning':

			default:
				confirmButtonColor = 'default';
				cancelButtonColor = 'default';
		}

		return (
			<Modal
				isOpen={isOpen}
				toggle={onCancel}
				className={classnames(combinedClass, {
					'fancy-modal--extra-small': size === 'extra-small',
					'fancy-modal--small': size === 'small',
					'fancy-modal--medium': size === 'medium',
					'fancy-modal--large': size === 'large',
					'fancy-modal--extra-large': size === 'extra-large',
					'fancy-modal--overflow-visible': overflowVisible,
				})}
			>
				<ModalBody className="fancy-modal__body">
					<ModalClose toggle={onCancel} />
					<h2 className="fancy-modal__title">{renderTitle()}</h2>
					<div className="fancy-modal__content">{renderContents()}</div>
					<div className="fancy-modal__footer">
						{renderButtons({
							onConfirm,
							onCancel,
							confirmButtonText,
							cancelButtonText,
							color,
							confirmButtonColor,
							cancelButtonColor,
							inProgress,
							cancelTestId,
							confirmTestId,
						})}
					</div>
				</ModalBody>
			</Modal>
		);
	}
}

export default Confirm;
